/**
 * Raising the engagement letter for an existing client.
 *
 * The sibling of `leads/SendEngagementDialog`, and deliberately a sibling rather than a
 * shared component with a counterparty prop. The two differ in the only part that
 * matters — who signs — and merging them would mean a conditional in the middle of the
 * step that decides where a signing link goes.
 *
 * ===========================================================================
 * WHO SIGNS, AND WHERE THAT ANSWER COMES FROM
 * ===========================================================================
 * For an INDIVIDUAL client, the server adds the primary contact as the required
 * signatory as part of drafting (`agreements.services._add_individual_client_signatory`).
 * Not here: a signatory decided in a browser is a signatory that depends on which screen
 * was used, and the same draft raised through the API would arrive with nobody on it.
 * This dialog says what will happen, so the person pressing the button is not surprised
 * by a signatory they did not add.
 *
 * For an ORGANISATION, nothing is added. Who may bind a company is a decision — a
 * director, a company secretary, two of them jointly — and not always the person whose
 * details sit in the contact record. The contacts are shown so the next screen's "Add
 * signatory" is a short step, never pre-filled into a commitment.
 *
 * ===========================================================================
 * WHY IT DRAFTS RATHER THAN SENDS
 * ===========================================================================
 * Issuing freezes the text and puts a document in somebody's hands; recipients are fixed
 * at that point and an amendment means a new version. That is not a thing to do in the
 * same click as writing it. One press, one consequence.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type Agreement,
  type AgreementTemplate,
  type ClientDetail,
} from '@legalcrm/api';
import {
  Form,
  FormActions,
  FormInput,
  Modal,
  Select,
  Textarea,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const schema = z.object({
  title: z.string().min(1, 'Give the letter a title'),
});

type FormValues = z.infer<typeof schema>;

/** Exactly one of `body` / `template` reaches the API, never both. */
type DraftPayload =
  | { client: string; title: string; body: string }
  | { client: string; title: string; template: string };

const FREE_TEXT = '';

export function DraftEngagementDialog({
  client,
  open,
  onClose,
}: {
  client: ClientDetail;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [templateId, setTemplateId] = useState(FREE_TEXT);
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);

  const templates = useApiQuery<AgreementTemplate[]>(
    qk.list('agreement-template-options'),
    STAFF.agreementTemplateOptions,
    { enabled: open },
  );
  const available = templates.data ?? [];
  const selected = available.find((template) => template.id === templateId) ?? null;

  const primary = client.contacts.find((contact) => contact.is_primary);
  const isIndividual = client.client_type === 'individual';

  const form = useZodForm(schema, {
    values: {
      // Named after the work, not the record. This title appears on the client's own copy
      // and in their portal, where "Client C-00042" reads as a database row.
      title: `Engagement letter — ${client.display_name}`,
    },
  });

  const draft = useApiMutation<Agreement, DraftPayload>(
    {
      mutationFn: (payload) => api.post<Agreement>(`${STAFF.agreements}/`, payload),
      // The client's own readiness panel is derived from whether a letter is signed, so
      // it is stale the moment one exists.
      invalidates: [
        qk.all('agreements'),
        qk.detail('clients', client.id),
        qk.sub('clients', client.id, 'agreements'),
      ],
    },
    {
      onSuccess: (agreement) => {
        toast({
          title: 'Draft created',
          description: 'Nothing has been sent yet — issue it when the wording is right.',
          tone: 'success',
        });
        onClose();
        navigate(`/agreements/${agreement.id}`);
      },
      onError: (error) =>
        toast({
          title: 'The draft was refused',
          description: error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  const handleSubmit = (values: FormValues) => {
    if (selected) {
      setBodyError(null);
      return draft.mutateAsync({ client: client.id, title: values.title, template: selected.id });
    }
    if (!body.trim()) {
      setBodyError('The letter needs its terms.');
      return;
    }
    setBodyError(null);
    return draft.mutateAsync({ client: client.id, title: values.title, body });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Engagement letter"
      description={`For ${client.display_name}. This creates a draft — nothing is sent until you issue it.`}
    >
      <Form form={form} ariaLabel="Engagement letter" error={draft.error} onSubmit={handleSubmit}>
        <FormInput form={form} name="title" label="Title" autoComplete="off" />

        <Select
          label="Start from"
          value={templateId}
          onChange={(event) => setTemplateId(event.currentTarget.value)}
          options={[
            { value: FREE_TEXT, label: 'Write it myself' },
            ...available.map((template) => ({ value: template.id, label: template.name })),
          ]}
          hint={
            available.length === 0 && !templates.isLoading
              ? 'No active templates yet — a firm administrator can add one under Settings > Agreement templates.'
              : 'A saved template is used exactly as written. To change the wording for this client, write it yourself instead.'
          }
        />

        {selected ? (
          // Read-only: `draft()` copies the template's stored body, not whatever might be
          // typed into a box like this, so an edit here would have nowhere to go.
          <div>
            <p className="mb-1 text-sm text-ink">Wording (from &quot;{selected.name}&quot;)</p>
            <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-line bg-surface-sunken p-3 text-sm text-ink-muted">
              {selected.body}
            </div>
          </div>
        ) : (
          <Textarea
            label="Terms"
            rows={14}
            value={body}
            onChange={(event) => {
              setBody(event.currentTarget.value);
              if (bodyError) setBodyError(null);
            }}
            error={bodyError ?? undefined}
            hint="Paste your standard engagement wording. Once it is issued the text is frozen — an amendment means a new version, so it is worth reading before it goes."
          />
        )}

        <Signatory client={client} isIndividual={isIndividual} primary={primary} />

        <FormActions submitLabel="Create the draft" busy={draft.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}

/**
 * What will happen about signatories, said before it happens.
 *
 * Three genuinely different situations, and each needs a different next step — so this
 * says which one the reader is in rather than showing a hopeful default.
 */
function Signatory({
  client,
  isIndividual,
  primary,
}: {
  client: ClientDetail;
  isIndividual: boolean;
  primary: ClientDetail['contacts'][number] | undefined;
}) {
  if (isIndividual && primary && (primary.email || primary.phone)) {
    return (
      <div className="rounded border bg-surface-sunken px-3 py-2 text-sm">
        <p className="text-ink">
          <span className="font-medium">{primary.full_name}</span> will be added as the
          required signatory.
        </p>
        <p className="mt-0.5 text-ink-muted">
          {primary.email || primary.phone} — taken from the primary contact on this client,
          so there is nothing to retype. You can add more signatories on the next screen.
        </p>
      </div>
    );
  }

  if (isIndividual) {
    return (
      <div className="rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-ink">
        This client has no primary contact with an email address or telephone number, so
        nobody can be added automatically. Add a signatory on the next screen — an
        agreement cannot be issued without one.
      </div>
    );
  }

  return (
    <div className="rounded border bg-surface-sunken px-3 py-2 text-sm">
      <p className="text-ink">
        Nobody is added automatically for an organisation. Who may bind {client.display_name}{' '}
        is the firm&apos;s decision, not a lookup — you name the signatory on the next screen.
      </p>
      {client.contacts.length > 0 && (
        <ul className="mt-1.5 flex flex-col gap-0.5 text-ink-muted">
          {client.contacts.map((contact) => (
            <li key={contact.id}>
              {contact.full_name}
              {contact.email && <span> — {contact.email}</span>}
              {contact.is_primary && <span className="text-ink-subtle"> (primary)</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
